import { isCapacitorNative } from '../api/ZionServer.ts'
import { isAppleDevice } from './layout.ts'
import { isStandalonePwa } from './router.ts'

function scrollMessagesToBottom(messages: HTMLElement | null, smooth = false): void {
  if (!messages) return
  const top = messages.scrollHeight
  if (smooth) {
    messages.scrollTo({ top, behavior: 'smooth' })
  } else {
    messages.scrollTop = top
  }
}

export function isIosStandalonePwa(): boolean {
  if (isCapacitorNative()) return false
  if (document.documentElement.classList.contains('web-browser')) return false
  return isAppleDevice() && isStandalonePwa()
}

function resetSafariScroll(): void {
  window.scrollTo(0, 0)
  document.body.scrollTop = 0
  document.documentElement.scrollTop = 0
}

/**
 * iOS PWA: footer fisso + translateY (come Capacitor), niente pin di #app.
 */
export function setupPWAKeyboardFix(opts?: {
  inputBar?: HTMLElement | null
  inputElement?: HTMLElement | null
  messages?: HTMLElement | null
}): () => void {
  if (!isIosStandalonePwa()) return () => {}

  const inputBar =
    opts?.inputBar ??
    (document.querySelector('#chat-input-bar') as HTMLElement | null) ??
    (document.querySelector('.chat-input-bar') as HTMLElement | null)
  const inputElement =
    opts?.inputElement ??
    (document.querySelector('#msg-input') as HTMLElement | null) ??
    (document.querySelector('.chat-input-field') as HTMLElement | null)
  const messages =
    opts?.messages ?? (document.querySelector('#messages') as HTMLElement | null)

  if (!inputBar) return () => {}

  const root = document.documentElement
  root.classList.add('ios-pwa-keyboard-fix')
  inputBar.classList.add('chat-input-bar--pwa-kb')

  const vv = window.visualViewport
  let raf = 0
  let observer: MutationObserver | null = null
  let baselineLayoutH = window.innerHeight || document.documentElement.clientHeight

  const applyLift = (keyboardOffset: number, keyboardOpen: boolean): void => {
    const lift = Math.max(0, Math.round(keyboardOffset))
    root.classList.toggle('keyboard-open', keyboardOpen)
    if (keyboardOpen && lift > 0) {
      root.style.setProperty('--keyboard-inset', `${lift}px`)
      inputBar.style.transform = `translateY(-${lift}px)`
      inputBar.style.paddingBottom = '8px'
    } else {
      root.style.removeProperty('--keyboard-inset')
      inputBar.style.transform = 'translateY(0px)'
      inputBar.style.removeProperty('padding-bottom')
    }
  }

  const nudgeMessages = (): void => {
    scrollMessagesToBottom(messages, false)
    requestAnimationFrame(() => scrollMessagesToBottom(messages, false))
  }

  const syncViewport = (): void => {
    if (raf) cancelAnimationFrame(raf)
    raf = requestAnimationFrame(() => {
      raf = 0
      const layoutH = window.innerHeight || document.documentElement.clientHeight
      const vvH = vv?.height ?? layoutH
      const offsetTop = vv?.offsetTop ?? 0
      const vvInset = Math.max(0, Math.round(layoutH - vvH - offsetTop))
      const layoutInset = Math.max(0, Math.round(baselineLayoutH - layoutH))
      const keyboardOffset = Math.max(vvInset, layoutInset)
      const focused = inputElement != null && document.activeElement === inputElement
      const keyboardOpen = keyboardOffset > 40 || (focused && keyboardOffset > 20)

      if (!keyboardOpen) {
        baselineLayoutH = layoutH
      }

      applyLift(keyboardOffset, keyboardOpen)

      if (focused || keyboardOpen) nudgeMessages()
    })
  }

  const onVvScroll = (): void => {
    if (inputElement && document.activeElement === inputElement) {
      resetSafariScroll()
      syncViewport()
    }
  }

  const onBlur = (): void => {
    window.setTimeout(() => {
      if (inputElement && document.activeElement === inputElement) return
      baselineLayoutH = window.innerHeight || document.documentElement.clientHeight
      applyLift(0, false)
      resetSafariScroll()
    }, 80)
  }

  const onFocus = (): void => {
    baselineLayoutH = window.innerHeight || document.documentElement.clientHeight
    syncViewport()
    for (const ms of [50, 120, 250, 400]) {
      window.setTimeout(() => {
        syncViewport()
        nudgeMessages()
      }, ms)
    }
  }

  if (messages) {
    observer = new MutationObserver(() => {
      if (
        root.classList.contains('keyboard-open') ||
        (inputElement && document.activeElement === inputElement)
      ) {
        nudgeMessages()
      }
    })
    observer.observe(messages, { childList: true, subtree: true })
  }

  if (vv) {
    vv.addEventListener('resize', syncViewport)
    vv.addEventListener('scroll', onVvScroll)
  }
  window.addEventListener('resize', syncViewport)
  inputElement?.addEventListener('blur', onBlur)
  inputElement?.addEventListener('focus', onFocus)
  syncViewport()

  return () => {
    if (raf) cancelAnimationFrame(raf)
    observer?.disconnect()
    if (vv) {
      vv.removeEventListener('resize', syncViewport)
      vv.removeEventListener('scroll', onVvScroll)
    }
    window.removeEventListener('resize', syncViewport)
    inputElement?.removeEventListener('blur', onBlur)
    inputElement?.removeEventListener('focus', onFocus)
    inputBar.style.removeProperty('transform')
    inputBar.style.removeProperty('padding-bottom')
    inputBar.classList.remove('chat-input-bar--pwa-kb')
    root.classList.remove('ios-pwa-keyboard-fix')
    root.classList.remove('keyboard-open')
    root.style.removeProperty('--keyboard-inset')
  }
}

/** Scroll messaggi in fondo se tastiera PWA aperta (es. dopo paint). */
export function scrollIosPwaMessagesIfKeyboardOpen(messages?: HTMLElement | null): void {
  if (!isIosStandalonePwa()) return
  if (!document.documentElement.classList.contains('keyboard-open')) return
  const box =
    messages ?? (document.querySelector('#messages') as HTMLElement | null)
  scrollMessagesToBottom(box, false)
}
